<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯會員等級' : '新增會員等級' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          {{ loading ? '處理中...' : (isEdit ? '更新' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="tier-form-layout">
        <div class="form-col">
      <form @submit.prevent="handleSubmit">
        <div class="form-card">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="tierCode" class="required">等級代碼</label>
              <input
                id="tierCode"
                v-model="form.tierCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.tierCode }"
                placeholder="例如：regular, silver, gold"
                :disabled="isEdit"
                required
                pattern="^[a-z0-9_]+$"
                maxlength="20"
              />
              <div v-if="errors.tierCode" class="invalid-feedback">
                {{ errors.tierCode }}
              </div>
              <p v-if="isEdit" class="form-hint">等級代碼無法修改</p>
              <p v-else class="form-hint">只能使用小寫字母、數字和底線</p>
            </div>

            <div class="form-group">
              <label for="tierName" class="required">等級名稱</label>
              <input
                id="tierName"
                v-model="form.tierName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.tierName }"
                placeholder="例如：一般會員"
                required
                maxlength="100"
              />
              <div v-if="errors.tierName" class="invalid-feedback">
                {{ errors.tierName }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="tierNameEn">英文名稱</label>
              <input
                id="tierNameEn"
                v-model="form.tierNameEn"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.tierNameEn }"
                placeholder="例如：Regular Member"
                maxlength="100"
              />
              <div v-if="errors.tierNameEn" class="invalid-feedback">
                {{ errors.tierNameEn }}
              </div>
            </div>

            <div class="form-group">
              <label for="displayOrder" class="required">顯示順序</label>
              <input
                id="displayOrder"
                v-model.number="form.displayOrder"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.displayOrder }"
                placeholder="數字越小越前面"
                required
                min="0"
              />
              <div v-if="errors.displayOrder" class="invalid-feedback">
                {{ errors.displayOrder }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="color">顏色</label>
              <div class="color-input-group">
                <input
                  id="color"
                  v-model="form.color"
                  type="color"
                  class="form-control color-picker"
                  :class="{ 'is-invalid': errors.color }"
                />
                <input
                  v-model="form.color"
                  type="text"
                  class="form-control color-text"
                  placeholder="#4CAF50"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  maxlength="7"
                />
              </div>
              <div v-if="errors.color" class="invalid-feedback">
                {{ errors.color }}
              </div>
            </div>

            <div class="form-group">
              <label>會員卡圖片</label>
              <div class="card-upload-area">
                <!-- 信用卡比例預覽（85.6:54 ≈ 1.586:1，padding-top=63.08%） -->
                <div class="card-preview-wrap">
                  <img
                    v-if="form.memberCardImageUrl"
                    :src="form.memberCardImageUrl"
                    class="card-preview-img"
                    alt="會員卡預覽"
                  />
                  <div v-else class="card-preview-placeholder">
                    <i class="ri-image-add-line"></i>
                    <span>會員卡圖片預覽</span>
                    <small>建議尺寸：856 × 540px</small>
                  </div>
                  <div v-if="cardImageUploading" class="card-preview-overlay">
                    <div class="upload-spinner"></div>
                    <span>上傳中...</span>
                  </div>
                </div>
                <div class="card-upload-actions">
                  <label class="btn btn-secondary btn-upload-label" :class="{ disabled: cardImageUploading }">
                    <i class="ri-upload-cloud-line"></i>
                    {{ form.memberCardImageUrl ? '更換圖片' : '上傳圖片' }}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      class="hidden-input"
                      :disabled="cardImageUploading"
                      @change="handleCardImageUpload"
                    />
                  </label>
                  <button
                    v-if="form.memberCardImageUrl"
                    type="button"
                    class="btn btn-danger-outline"
                    @click="form.memberCardImageUrl = ''"
                  >
                    <i class="ri-delete-bin-line"></i>
                    移除
                  </button>
                </div>
                <p class="form-hint">支援 JPG、PNG、WebP，最大 5MB。圖片將依信用卡比例（85.6×54mm）顯示於會員 APP</p>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <h3 class="section-title">等級條件</h3>

          <div class="condition-info-box">
            <p class="condition-info-title">
              <i class="ri-information-line"></i>
              升等條件說明
            </p>
            <ul class="condition-info-list">
              <li>
                <strong>累積條件</strong>：最低消費金額與最低消費次數的判斷邏輯可選擇
                <strong>且（AND）</strong> 或 <strong>或（OR）</strong>。設為 <code>0</code> 表示不檢查該項目。
              </li>
              <li>
                <strong>單次消費升等（或）</strong>：若啟用直接升級，會員<strong>單筆訂單</strong>消費達到門檻即可升等，
                與累積條件為<strong>擇一符合</strong>的關係。
              </li>
              <li>
                系統依<strong>顯示順序</strong>從高到低判定，會員會落在符合條件的最高等級。
              </li>
              <li>
                訂單完成後會即時重算；若符合更高等級將自動升等。
              </li>
            </ul>
          </div>

          <p class="subsection-label">累積條件</p>
          <div class="form-row">
            <div class="form-group">
              <label for="minSpentAmount">最低消費金額</label>
              <input
                id="minSpentAmount"
                v-model.number="form.minSpentAmount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.minSpentAmount }"
                placeholder="0"
                min="0"
                step="1"
              />
              <div v-if="errors.minSpentAmount" class="invalid-feedback">
                {{ errors.minSpentAmount }}
              </div>
              <p class="form-hint">會員累積消費金額須達此門檻；設為 0 表示不限制金額</p>
            </div>

            <div class="form-group">
              <label for="minPurchaseCount">最低消費次數</label>
              <input
                id="minPurchaseCount"
                v-model.number="form.minPurchaseCount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.minPurchaseCount }"
                placeholder="0"
                min="0"
              />
              <div v-if="errors.minPurchaseCount" class="invalid-feedback">
                {{ errors.minPurchaseCount }}
              </div>
              <p class="form-hint">會員累積消費次數須達此門檻；設為 0 表示不限制次數</p>
            </div>
          </div>

          <div class="logic-toggle-row">
            <span class="logic-toggle-label">兩個條件的關係</span>
            <div class="logic-toggle-btns">
              <button
                type="button"
                class="logic-btn"
                :class="{ active: form.accumulativeLogic === 'AND' }"
                @click="form.accumulativeLogic = 'AND'"
              >
                <strong>且（AND）</strong>
                <span>金額與次數須同時達標</span>
              </button>
              <button
                type="button"
                class="logic-btn"
                :class="{ active: form.accumulativeLogic === 'OR' }"
                @click="form.accumulativeLogic = 'OR'"
              >
                <strong>或（OR）</strong>
                <span>金額或次數任一達標即可</span>
              </button>
            </div>
            <p class="form-hint" v-if="form.minSpentAmount === 0 || form.minPurchaseCount === 0">
              其中一項設為 0（不限制）時，此設定僅對兩項都大於 0 的情況有效。
            </p>
          </div>

          <p class="subsection-label">單次消費升等（或）</p>
          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="form.directUpgradeEnabled"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span>啟用單次消費直接升等</span>
              </label>
              <p class="form-hint">
                會員單筆訂單消費達到指定金額，即可直接升級到此等級（不需同時符合上方累積條件）
              </p>
            </div>
          </div>

          <div v-if="form.directUpgradeEnabled" class="form-row">
            <div class="form-group">
              <label for="directUpgradeAmount" class="required">單次消費升等門檻</label>
              <input
                id="directUpgradeAmount"
                v-model.number="form.directUpgradeAmount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.directUpgradeAmount }"
                placeholder="例如：50000"
                min="0"
                step="1"
              />
              <div v-if="errors.directUpgradeAmount" class="invalid-feedback">
                {{ errors.directUpgradeAmount }}
              </div>
              <p class="form-hint">單筆訂單實付金額達此門檻即可升等（例：一次消費 50,000 元直接升金卡）</p>
            </div>
          </div>

          <p class="subsection-label">降等設定</p>
          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="form.autoDowngradeEnabled"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span>允許此等級被自動降等</span>
              </label>
              <p class="form-hint">
                啟用後，當「系統參數 → 會員等級」的年度自動降等機制觸發時，未達標的會員將從此等級降等。
                停用後，即使全域機制啟用，此等級的會員仍會保留等級（適用於手動授予的終身 VIP 等級）。
              </p>
            </div>
          </div>
        </div>

        <div class="form-card">
          <h3 class="section-title">等級權益</h3>
          
          <div class="form-group">
            <label for="benefits">權益列表</label>
            <textarea
              id="benefits"
              v-model="form.benefits"
              class="form-control"
              :class="{ 'is-invalid': errors.benefits }"
              rows="4"
              placeholder="每行一個權益，例如：&#10;生日優惠券&#10;每月專屬優惠&#10;優先預約"
            ></textarea>
            <div v-if="errors.benefits" class="invalid-feedback">
              {{ errors.benefits }}
            </div>
            <p class="form-hint">每行一個權益項目</p>
          </div>
        </div>

        <div class="form-card">
          <h3 class="section-title">狀態</h3>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="form.isActive"
                type="checkbox"
                class="form-checkbox"
              />
              <span>啟用此等級</span>
            </label>
            <p class="form-hint">停用的等級將不會顯示在會員選擇列表中</p>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
      </form>
        </div><!-- end form-col -->

        <!-- 右側：升降等行為說明 -->
        <div class="summary-col">
          <div class="behavior-panel">
            <div class="behavior-panel-header">
              <i class="ri-information-line"></i>
              <span>目前設定的效果</span>
            </div>

            <!-- 升等判定 -->
            <div class="behavior-section">
              <p class="behavior-section-title">
                <i class="ri-arrow-up-circle-line"></i>
                升等判定
              </p>
              <template v-if="tierSummary.upgradeConditions.length === 0">
                <p class="behavior-text muted">未設定升等條件（所有會員預設符合）</p>
              </template>
              <template v-else>
                <p class="behavior-text" v-if="tierSummary.accumulativeDesc">
                  <strong>累積條件：</strong>{{ tierSummary.accumulativeDesc }}
                </p>
                <p class="behavior-text" v-if="tierSummary.directDesc">
                  <strong>單次升等：</strong>{{ tierSummary.directDesc }}
                </p>
                <p class="behavior-text" v-if="tierSummary.accumulativeDesc && tierSummary.directDesc">
                  <i class="ri-git-branch-line"></i>
                  兩者符合其一即可升等
                </p>
              </template>
            </div>

            <!-- 計算週期 -->
            <div class="behavior-section">
              <p class="behavior-section-title">
                <i class="ri-calendar-event-line"></i>
                計算週期
                <a href="/client/settings?tab=member_tier" target="_blank" class="settings-link">
                  <i class="ri-settings-3-line"></i>系統參數
                </a>
              </p>
              <div class="behavior-badge" :class="tierSummary.modeClass">
                {{ tierSummary.modeLabel }}
              </div>
              <p class="behavior-text">{{ tierSummary.modeDesc }}</p>
            </div>

            <!-- 自動降等 -->
            <div class="behavior-section">
              <p class="behavior-section-title">
                <i class="ri-arrow-down-circle-line"></i>
                自動降等
              </p>
              <div class="behavior-badge" :class="tierSummary.downgradeClass">
                {{ tierSummary.downgradeLabel }}
              </div>
              <p class="behavior-text">{{ tierSummary.downgradeDesc }}</p>
            </div>

            <!-- 案例解說 -->
            <div class="behavior-section behavior-example">
              <p class="behavior-section-title">
                <i class="ri-user-line"></i>
                案例解說
              </p>
              <div class="example-story">
                <p v-for="(line, i) in tierSummary.exampleLines" :key="i" class="example-line">
                  {{ line }}
                </p>
              </div>
            </div>
          </div>
        </div><!-- end summary-col -->

      </div><!-- end tier-form-layout -->
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { showError, showSuccess } from '@/utils/swal.js';

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export default {
  name: 'ClientMemberTierCreate',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isEdit = computed(() => !!route.params.id);
    const { pageTitle } = usePageTitle(isEdit.value ? '編輯會員等級' : '新增會員等級');
    
    const form = ref({
      tierCode: '',
      tierName: '',
      tierNameEn: '',
      displayOrder: 0,
      iconUrl: '',
      color: '#4CAF50',
      memberCardImageUrl: '',
      minSpentAmount: 0,
      minPurchaseCount: 0,
      accumulativeLogic: 'AND',
      benefits: '',
      isActive: true,
      autoDowngradeEnabled: false,
      directUpgradeEnabled: false,
      directUpgradeAmount: null
    });

    const errors = ref({});
    const errorMessage = ref('');
    const loading = ref(false);
    const cardImageUploading = ref(false);

    const loadTier = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await memberTierService.getTier(parseInt(route.params.id, 10));

        if (response && response.success && response.data) {
          const tier = response.data;
          form.value = {
            tierCode: tier.tierCode || '',
            tierName: tier.tierName || '',
            tierNameEn: tier.tierNameEn || '',
            displayOrder: tier.displayOrder || 0,
            iconUrl: tier.iconUrl || '',
            color: tier.color || '#4CAF50',
            memberCardImageUrl: tier.memberCardImageUrl || '',
            minSpentAmount: tier.minSpentAmount || 0,
            minPurchaseCount: tier.minPurchaseCount || 0,
            accumulativeLogic: tier.accumulativeLogic || 'AND',
            benefits: tier.benefits || '',
            isActive: tier.isActive !== undefined ? tier.isActive : true,
            autoDowngradeEnabled: tier.autoDowngradeEnabled !== undefined ? tier.autoDowngradeEnabled : false,
            directUpgradeEnabled: tier.directUpgradeEnabled !== undefined ? tier.directUpgradeEnabled : false,
            directUpgradeAmount: tier.directUpgradeAmount || null
          };
        } else {
          errorMessage.value = response?.message || '獲取會員等級資料失敗';
          await showError('載入失敗', errorMessage.value);
        }
      } catch (err) {
        console.error('獲取會員等級資料失敗:', err);
        errorMessage.value = err.message || '獲取會員等級資料失敗';
        await showError('載入失敗', errorMessage.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      errors.value = {};
      errorMessage.value = '';
      loading.value = true;

      try {
        // 驗證表單
        if (!form.value.tierCode && !isEdit.value) {
          errors.value.tierCode = '等級代碼為必填';
          return;
        }
        if (!form.value.tierName) {
          errors.value.tierName = '等級名稱為必填';
          return;
        }
        if (form.value.displayOrder < 0) {
          errors.value.displayOrder = '顯示順序必須大於等於 0';
          return;
        }
        if (form.value.directUpgradeEnabled) {
          const amount = Math.floor(parseFloat(form.value.directUpgradeAmount || 0));
          if (!amount || amount <= 0) {
            errors.value.directUpgradeAmount = '啟用單次消費升等時，門檻金額必須大於 0';
            return;
          }
          form.value.directUpgradeAmount = amount;
        } else {
          form.value.directUpgradeAmount = null;
        }
        form.value.minSpentAmount = Math.floor(parseFloat(form.value.minSpentAmount || 0));

        let response;
        if (isEdit.value) {
          // 更新
          const updateData = { ...form.value };
          delete updateData.tierCode; // 等級代碼不可修改
          response = await memberTierService.updateTier(parseInt(route.params.id, 10), updateData);
        } else {
          // 新增
          response = await memberTierService.createTier(form.value);
        }

        if (response && response.success) {
          await showSuccess('成功', isEdit.value ? '會員等級更新成功' : '會員等級建立成功');
          router.push('/client/member-tiers');
        } else {
          errorMessage.value = response?.message || (isEdit.value ? '更新失敗' : '建立失敗');
          await showError(isEdit.value ? '更新失敗' : '建立失敗', errorMessage.value);
        }
      } catch (err) {
        console.error('提交失敗:', err);
        errorMessage.value = err.message || (isEdit.value ? '更新失敗' : '建立失敗');
        
        // 處理驗證錯誤
        if (err.response?.data?.error?.details) {
          const details = err.response.data.error.details;
          if (details.validationErrors && Array.isArray(details.validationErrors)) {
            details.validationErrors.forEach(error => {
              if (error.field) {
                errors.value[error.field] = error.message;
              }
            });
          } else if (typeof details === 'object') {
            Object.keys(details).forEach(key => {
              errors.value[key] = Array.isArray(details[key]) ? details[key][0] : details[key];
            });
          }
        }
        
        await showError(isEdit.value ? '更新失敗' : '建立失敗', errorMessage.value);
      } finally {
        loading.value = false;
      }
    };

    const handleCardImageUpload = async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        await showError('檔案類型錯誤', '僅支援 JPG、PNG、WebP 格式');
        return;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        await showError('檔案過大', '圖片大小不能超過 5MB');
        return;
      }

      cardImageUploading.value = true;
      try {
        const res = await memberTierService.uploadCardImage(file);
        if (res?.success && res?.data?.fileUrl) {
          form.value.memberCardImageUrl = res.data.fileUrl;
        } else {
          await showError('上傳失敗', res?.message || '無法取得圖片 URL');
        }
      } catch (err) {
        await showError('上傳失敗', err.message || '上傳發生錯誤');
      } finally {
        cardImageUploading.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/member-tiers');
    };

    // 全域會員等級計算模式設定（從系統參數讀取）
    const clientTierSettings = reactive({
      calculationMode: 'lifetime',
      annualDowngradeEnabled: false,
      annualDowngradeMonth: 1,
    });

    const loadClientTierSettings = async () => {
      try {
        const res = await clientSettingsService.get(NAMESPACES.MEMBER_TIER);
        const s = res?.data?.settings ?? {};
        clientTierSettings.calculationMode = s.calculationMode ?? 'lifetime';
        clientTierSettings.annualDowngradeEnabled = s.annualDowngradeEnabled ?? false;
        clientTierSettings.annualDowngradeMonth = s.annualDowngradeMonth ?? 1;
      } catch {
        // 保留預設值
      }
    };

    const fmt = (n) => {
      const num = Math.floor(parseFloat(n || 0));
      return num >= 10000
        ? `${(num / 10000).toFixed(num % 10000 === 0 ? 0 : 1)} 萬元`
        : `${num.toLocaleString()} 元`;
    };

    const tierSummary = computed(() => {
      const f = form.value;
      const cs = clientTierSettings;
      const tierName = f.tierName || '此等級';

      const hasSpent = (f.minSpentAmount || 0) > 0;
      const hasCount = (f.minPurchaseCount || 0) > 0;
      const logic = f.accumulativeLogic === 'OR' ? '或' : '且';

      // 累積條件描述
      let accumulativeDesc = '';
      if (hasSpent && hasCount) {
        accumulativeDesc = `累積消費 ${fmt(f.minSpentAmount)} ${logic} 消費 ${f.minPurchaseCount} 次`;
      } else if (hasSpent) {
        accumulativeDesc = `累積消費滿 ${fmt(f.minSpentAmount)}`;
      } else if (hasCount) {
        accumulativeDesc = `累積消費 ${f.minPurchaseCount} 次`;
      }

      // 單次升等描述
      const directDesc =
        f.directUpgradeEnabled && (f.directUpgradeAmount || 0) > 0
          ? `單筆訂單實付 ${fmt(f.directUpgradeAmount)} 直接升等`
          : '';

      const upgradeConditions = [accumulativeDesc, directDesc].filter(Boolean);

      // 計算週期
      const modeMap = {
        lifetime: { label: '終身累積', cls: 'badge-blue', desc: '會員消費記錄永久累積，不因時間重置。升等後長期保留，除非手動調整。' },
        annual_calendar: { label: `年度固定月份（每年 ${cs.annualDowngradeMonth} 月）`, cls: 'badge-orange', desc: `每年 ${cs.annualDowngradeMonth} 月系統重新審核所有會員的累積資料，判定是否維持、升等或降等。` },
        annual_rolling: { label: '升等後一年', cls: 'badge-purple', desc: '升等後一年為計算週期，期滿時系統重新以當時累積資料評定等級。' },
      };
      const mode = modeMap[cs.calculationMode] ?? modeMap.lifetime;

      // 降等狀態
      let downgradeLabel, downgradeClass, downgradeDesc;
      if (cs.calculationMode === 'lifetime') {
        downgradeLabel = '不會自動降等';
        downgradeClass = 'badge-green';
        downgradeDesc = '計算模式為「終身累積」，系統不執行自動降等。';
      } else if (!cs.annualDowngradeEnabled) {
        downgradeLabel = '不會自動降等';
        downgradeClass = 'badge-green';
        downgradeDesc = '系統參數中「自動降等」未啟用，即使未達標也不會降等。';
      } else if (!f.autoDowngradeEnabled) {
        downgradeLabel = '此等級受保護';
        downgradeClass = 'badge-yellow';
        downgradeDesc = '全域自動降等已啟用，但此等級設為「不允許降等」，會員將長期保留此等級。';
      } else {
        downgradeLabel = '會自動降等';
        downgradeClass = 'badge-red';
        downgradeDesc = cs.calculationMode === 'annual_calendar'
          ? `每年 ${cs.annualDowngradeMonth} 月審核，未達升等條件的會員將自動降至符合資格的等級。`
          : '升等後一年期滿時審核，未達升等條件的會員將自動降至符合資格的等級。';
      }

      // 案例文字
      const exampleLines = [];
      const condDesc = upgradeConditions.length > 0
        ? upgradeConditions.join('，或')
        : '無條件限制';

      exampleLines.push(`王小明在 2024/6/1 達成「${condDesc}」，系統自動升為${tierName}。`);

      if (cs.calculationMode === 'lifetime') {
        exampleLines.push('由於計算模式為終身累積，等級長期保留，不會因為時間過去而重置或降等。');
      } else if (cs.calculationMode === 'annual_calendar') {
        exampleLines.push(`每年 ${cs.annualDowngradeMonth} 月，系統重新審核王小明的消費記錄：`);
        if (!cs.annualDowngradeEnabled) {
          exampleLines.push('→ 即使未達標，由於「自動降等」未啟用，等級仍會保留。');
        } else if (!f.autoDowngradeEnabled) {
          exampleLines.push('→ 由於此等級設為「不允許降等」，即使未達標，等級仍會保留。');
        } else {
          exampleLines.push(`→ 若累積消費仍達標（${condDesc}），維持${tierName}。`);
          exampleLines.push(`→ 若未達標，自動降至符合資格的較低等級。`);
        }
      } else {
        exampleLines.push('升等後一年（2025/6/1）為審核期限：');
        if (!cs.annualDowngradeEnabled) {
          exampleLines.push('→ 即使期限到達未達標，由於「自動降等」未啟用，等級仍會保留。');
        } else if (!f.autoDowngradeEnabled) {
          exampleLines.push('→ 由於此等級設為「不允許降等」，期限到達後等級仍會保留。');
        } else {
          exampleLines.push(`→ 若期限內累積消費達標（${condDesc}），自動續等。`);
          exampleLines.push('→ 若未達標，自動降至符合資格的較低等級。');
        }
      }

      return {
        upgradeConditions,
        accumulativeDesc,
        directDesc,
        modeLabel: mode.label,
        modeClass: mode.cls,
        modeDesc: mode.desc,
        downgradeLabel,
        downgradeClass,
        downgradeDesc,
        exampleLines,
      };
    });

    onMounted(() => {
      loadClientTierSettings();
      if (isEdit.value) {
        loadTier();
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      isEdit,
      pageTitle,
      cardImageUploading,
      tierSummary,
      handleSubmit,
      handleCancel,
      handleCardImageUpload,
    };
  },
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.tier-form-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.form-col {
  flex: 1;
  min-width: 0;
}

.summary-col {
  flex-shrink: 0;
  width: 300px;
  align-self: flex-start;
}

@media (max-width: 1100px) {
  .tier-form-layout {
    flex-direction: column;
  }
  .summary-col {
    width: 100%;
  }
  .behavior-panel {
    position: static;
    width: 100%;
    max-height: none;
    overflow: visible;
  }
}

/* 右側行為說明面板 */
.behavior-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  position: fixed;
  top: 128px;
  right: 20px;
  width: 300px;
  max-height: calc(100vh - 144px);
  overflow-y: auto;
}

.behavior-panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 600;
  color: #495057;

  i {
    font-size: 15px;
    color: #667eea;
  }
}

.behavior-section {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.behavior-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;

  i {
    font-size: 14px;
  }
}

.settings-link {
  margin-left: auto;
  font-size: 11px;
  color: #667eea;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;

  &:hover {
    text-decoration: underline;
  }
}

.behavior-text {
  font-size: 13px;
  color: #495057;
  margin: 6px 0 0 0;
  line-height: 1.5;

  strong {
    color: #212529;
  }

  i {
    color: #667eea;
    margin-right: 4px;
  }

  &.muted {
    color: #adb5bd;
    font-style: italic;
  }
}

.behavior-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 2px;

  &.badge-blue   { background: #e7f0ff; color: #3b5bdb; }
  &.badge-orange { background: #fff3e0; color: #e65100; }
  &.badge-purple { background: #f3e5f5; color: #7b1fa2; }
  &.badge-green  { background: #e8f5e9; color: #2e7d32; }
  &.badge-yellow { background: #fffde7; color: #f57f17; }
  &.badge-red    { background: #fce4ec; color: #c62828; }
}

.behavior-example {
  background: #fafafa;
}

.example-story {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px;
}

.example-line {
  font-size: 12px;
  color: #495057;
  line-height: 1.6;
  margin: 0 0 4px 0;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 16px;
}

.form-card:last-of-type { margin-bottom: 0; }

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-bottom: 0;
}

.condition-info-box {
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
}

.condition-info-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
}

.condition-info-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #374151;
  line-height: 1.7;
}

.condition-info-list code {
  background: #e5e7eb;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.subsection-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 12px 0;
  padding-top: 4px;
}

.condition-and-note {
  font-size: 12px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 12px;
  margin: -8px 0 20px 0;
}

.color-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.color-picker {
  width: 60px;
  height: 40px;
  padding: 2px;
  cursor: pointer;
}

.color-text {
  flex: 1;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-top: 16px;
}

.alert-danger {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }

/* ===== 會員卡圖片上傳 ===== */
.card-upload-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-preview-wrap {
  position: relative;
  width: 100%;
  max-width: 420px;
  aspect-ratio: 85.6 / 54; /* 標準信用卡比例 ISO 7810 ID-1 */
  border-radius: 12px;
  overflow: hidden;
  background: #f0f0f0;
  border: 2px dashed #ddd;
}

.card-preview-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.card-preview-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #aaa;

  i {
    font-size: 32px;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  small {
    font-size: 12px;
    opacity: 0.7;
  }
}

.card-preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: white;
  font-size: 14px;
  border-radius: 10px;
}

.upload-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.card-upload-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-upload-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.hidden-input {
  display: none;
}

.btn-danger-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background: transparent;
  color: #dc3545;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dc3545;
    color: white;
  }
}

/* AND / OR 邏輯切換 */
.logic-toggle-row {
  margin-top: 4px;
  margin-bottom: 16px;
}

.logic-toggle-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
}

.logic-toggle-btns {
  display: flex;
  gap: 10px;
}

.logic-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 14px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  strong {
    font-size: 13px;
    color: #495057;
  }

  span {
    font-size: 11px;
    color: #868e96;
  }

  &:hover {
    border-color: #667eea;
    background: #f8f9ff;
  }

  &.active {
    border-color: #667eea;
    background: #eef0fd;

    strong {
      color: #667eea;
    }

    span {
      color: #667eea;
    }
  }
}
</style>
